import React, { useMemo } from "react"
import GridHeader from "./GridHeader"
import GroupRow from "./GroupRow"
import PersonRow from "./PersonRow"
import FooterRow from "./FooterRow"
import NoWorkers from "./NoWorkers"
import { EMPTY } from "../hooks/useCellIndex"
import { formatHours, sumHours } from "../lib/hours"

const PeopleGrid = ({ board, users, cells, absences, selection, range, collapsed, onToggleGroup, template, density }) => {
  const { days } = board
  const weeks    = days.length / 7

  const sections = useMemo(() => board.groups
    .map(group => ({ group, members: users.filter(user => (user.group_id ?? null) === (group.id ?? null)) }))
    .filter(section => section.members.length > 0), [board.groups, users])

  const stats = useMemo(() => {
    const counts = {}
    const groupDays = {}
    const groupTotals = {}
    let total = 0
    const groupOf = new Map(users.map(user => [user.id, user.group_id ?? "none"]))
    days.forEach(day => {
      const planned = new Set()
      users.forEach(user => {
        const items = cells.get(`${user.id}|${day.date}`) || EMPTY
        if (items.length === 0) return
        planned.add(user.id)
        const key = groupOf.get(user.id)
        const hours = sumHours(items)
        total += hours
        groupTotals[key] = (groupTotals[key] || 0) + hours
        groupDays[key] = groupDays[key] || {}
        groupDays[key][day.date] = (groupDays[key][day.date] || 0) + 1
      })
      counts[day.date] = planned.size
    })
    return { counts, groupDays, groupTotals, total }
  }, [days, users, cells])

  return (
    <div className="min-w-[var(--grid-min)]">
      <GridHeader days={days} template={template} density={density} firstLabel={I18n.t("project_planning.columns.people", { count: users.length })} />

      {board.users.length === 0 && <NoWorkers />}

      {sections.map(({ group, members }) => {
        const key = group.id ?? "none"
        const isCollapsed = collapsed.has(key)
        return (
          <React.Fragment key={key}>
            <GroupRow
              group={group}
              size={members.length}
              days={days}
              template={template}
              collapsed={isCollapsed}
              onToggle={() => onToggleGroup(key)}
              plannedByDay={stats.groupDays[key] || {}}
              total={stats.groupTotals[key] || 0}
            />
            {!isCollapsed && members.map(user => (
              <PersonRow
                key={user.id}
                user={user}
                days={days}
                template={template}
                cells={cells}
                absences={absences}
                selection={selection}
                range={range}
                weeks={weeks}
                subtitle={I18n.t("project_planning.weekly_base", { hours: formatHours(user.weekly_hours) })}
              />
            ))}
          </React.Fragment>
        )
      })}

      {board.users.length > 0 && users.length === 0 && (
        <div className="px-6 py-10 text-center text-[13px] text-slate-400">{I18n.t("project_planning.no_match")}</div>
      )}

      <FooterRow days={days} template={template} label={I18n.t("project_planning.footer.people")} counts={stats.counts} total={stats.total} />
    </div>
  )
}

export default PeopleGrid
